import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Chip,
  Tooltip
} from '@mui/material';
import {
  CalendarToday as CalendarIcon,
  ShoppingCart as ReservationIcon,
  Build as ProductionIcon,
  LocalShipping as DeliveryIcon,
  CheckCircle as CompleteIcon
} from '@mui/icons-material';

/**
 * CampaignTimeline Component
 *
 * Visualizes the three phases of a campaign:
 * 1. Reservation Period - Customer pre-order window
 * 2. Production Period - Manufacturing after reservations close
 * 3. Delivery - Shipping to customers
 *
 * Props:
 * - campaign: Campaign object with timeline data
 * - variant: 'compact' | 'detailed' (default: 'detailed')
 */
const CampaignTimeline = ({ campaign, variant = 'detailed' }) => {
  if (!campaign || !campaign.timeline) {
    return null;
  }

  const { timeline } = campaign;

  // Calculate phase durations
  const reservationStart = new Date(timeline.reservationPeriod?.startDate || timeline.createdAt);
  const reservationEnd = new Date(timeline.reservationPeriod?.endDate || timeline.launchDate);
  const productionEnd = new Date(timeline.productionPeriod?.endDate || timeline.endDate);
  const deliveryEnd = new Date(timeline.delivery?.estimatedDeliveryDate || timeline.endDate);

  const today = new Date();

  // Determine current phase
  const getCurrentPhase = () => {
    if (today < reservationStart) return 'upcoming';
    if (today >= reservationStart && today <= reservationEnd) return 'reservation';
    if (today > reservationEnd && today <= productionEnd) return 'production';
    if (today > productionEnd && today <= deliveryEnd) return 'delivery';
    return 'completed';
  };

  const currentPhase = getCurrentPhase();

  // Calculate durations in days
  const reservationDays = Math.ceil((reservationEnd - reservationStart) / (1000 * 60 * 60 * 24));
  const productionDays = Math.ceil((productionEnd - reservationEnd) / (1000 * 60 * 60 * 24));
  const deliveryDays = Math.ceil((deliveryEnd - productionEnd) / (1000 * 60 * 60 * 24));
  const totalDays = reservationDays + productionDays + deliveryDays;

  // Calculate percentage widths for visualization
  const reservationWidth = (reservationDays / totalDays) * 100;
  const productionWidth = (productionDays / totalDays) * 100;
  const deliveryWidth = (deliveryDays / totalDays) * 100;

  // Phase configurations
  const phases = [
    {
      id: 'reservation',
      name: 'Reservation Period',
      icon: <ReservationIcon />,
      color: '#08D2FF',
      bgColor: 'rgba(8, 210, 255, 0.15)',
      startDate: reservationStart,
      endDate: reservationEnd,
      duration: reservationDays,
      width: reservationWidth,
      description: 'Customers place pre-orders'
    },
    {
      id: 'production',
      name: 'Production',
      icon: <ProductionIcon />,
      color: '#FF6B35',
      bgColor: 'rgba(255, 107, 53, 0.15)',
      startDate: reservationEnd,
      endDate: productionEnd,
      duration: productionDays,
      width: productionWidth,
      description: 'Manufacturing in progress'
    },
    {
      id: 'delivery',
      name: 'Delivery',
      icon: <DeliveryIcon />,
      color: '#4CAF50',
      bgColor: 'rgba(76, 175, 80, 0.15)',
      startDate: productionEnd,
      endDate: deliveryEnd,
      duration: deliveryDays,
      width: deliveryWidth,
      description: 'Shipping to customers'
    }
  ];

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  if (variant === 'compact') {
    return (
      <Box sx={{ width: '100%' }}>
        {/* Progress Bar */}
        <Box sx={{ display: 'flex', height: 8, borderRadius: '6px', overflow: 'hidden', mb: 1 }}>
          {phases.map((phase) => (
            <Tooltip key={phase.id} title={`${phase.name}: ${phase.duration} days`}>
              <Box
                sx={{
                  width: `${phase.width}%`,
                  bgcolor: phase.id === currentPhase ? phase.color : phase.bgColor,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    opacity: 0.8
                  }
                }}
              />
            </Tooltip>
          ))}
        </Box>

        {/* Phase Labels */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="caption" color="text.secondary">
            {formatDate(reservationStart)}
          </Typography>
          <Chip
            size="small"
            label={currentPhase === 'completed' ? 'Completed' : phases.find(p => p.id === currentPhase)?.name || 'Upcoming'}
            color={currentPhase === 'completed' ? 'success' : 'primary'}
            sx={{ textTransform: 'capitalize' }}
          />
          <Typography variant="caption" color="text.secondary">
            {formatDate(deliveryEnd)}
          </Typography>
        </Box>
      </Box>
    );
  }

  // Detailed variant
  return (
    <Paper sx={{ p: 3, bgcolor: 'rgba(255, 255, 255, 0.02)', border: '1px solid rgba(245, 248, 255, 0.08)' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <CalendarIcon sx={{ color: 'primary.main' }} />
          <Typography variant="h6">Campaign Timeline</Typography>
        </Box>
        {currentPhase === 'completed' ? (
          <Chip
            icon={<CompleteIcon />}
            label="Campaign Completed"
            color="success"
            variant="outlined"
          />
        ) : (
          <Chip
            label={`Current: ${phases.find(p => p.id === currentPhase)?.name || 'Upcoming'}`}
            color="primary"
          />
        )}
      </Box>

      {/* Timeline Visualization */}
      <Box sx={{ mb: 4 }}>
        {/* Progress Bar */}
        <Box sx={{ display: 'flex', height: 40, borderRadius: '6px', overflow: 'hidden', mb: 2, boxShadow: 2 }}>
          {phases.map((phase, index) => (
            <Tooltip
              key={phase.id}
              title={
                <Box sx={{ p: 0.5 }}>
                  <Typography variant="body2" fontWeight="bold">{phase.name}</Typography>
                  <Typography variant="caption">{phase.description}</Typography>
                  <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                    {formatDate(phase.startDate)} - {formatDate(phase.endDate)}
                  </Typography>
                  <Typography variant="caption" display="block">
                    Duration: {phase.duration} days
                  </Typography>
                </Box>
              }
            >
              <Box
                sx={{
                  width: `${phase.width}%`,
                  bgcolor: phase.id === currentPhase ? phase.color : phase.bgColor,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  position: 'relative',
                  transition: 'all 0.3s ease',
                  cursor: 'pointer',
                  borderLeft: index > 0 ? '2px solid rgba(255, 255, 255, 0.1)' : 'none',
                  '&:hover': {
                    bgcolor: phase.color,
                    transform: 'scaleY(1.1)'
                  }
                }}
              >
                {phase.width > 15 && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'white' }}>
                    {phase.icon}
                  </Box>
                )}
              </Box>
            </Tooltip>
          ))}
        </Box>

        {/* Date Markers */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', px: 0.5 }}>
          <Typography variant="caption" color="text.secondary" fontWeight="medium">
            {formatDate(reservationStart)}
          </Typography>
          <Typography variant="caption" color="text.secondary" fontWeight="medium">
            {formatDate(deliveryEnd)}
          </Typography>
        </Box>
      </Box>

      {/* Phase Details */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 2 }}>
        {phases.map((phase) => (
          <Box
            key={phase.id}
            sx={{
              p: 2,
              borderRadius: '6px',
              bgcolor: phase.id === currentPhase ? phase.bgColor : 'rgba(255, 255, 255, 0.02)',
              border: `1px solid ${phase.id === currentPhase ? phase.color : 'rgba(245, 248, 255, 0.08)'}`,
              transition: 'all 0.3s ease',
              '&:hover': {
                bgcolor: phase.bgColor,
                transform: 'translateY(-2px)',
                boxShadow: `0 4px 12px ${phase.color}40`
              }
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Box sx={{ color: phase.color }}>
                {phase.icon}
              </Box>
              <Typography variant="subtitle2" fontWeight="bold">
                {phase.name}
              </Typography>
            </Box>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
              {phase.description}
            </Typography>
            <Typography variant="body2" fontWeight="medium">
              {phase.duration} days
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block">
              {formatDate(phase.startDate)} - {formatDate(phase.endDate)}
            </Typography>
            {phase.id === currentPhase && (
              <Chip
                size="small"
                label="Active"
                sx={{ mt: 1, bgcolor: phase.color, color: 'white', fontWeight: 'bold' }}
              />
            )}
          </Box>
        ))}
      </Box>

      {/* Summary Stats */}
      <Box sx={{ mt: 3, pt: 3, borderTop: '1px solid rgba(245, 248, 255, 0.08)' }}>
        <Box sx={{ display: 'flex', gap: 4, justifyContent: 'center', flexWrap: 'wrap' }}>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h4" color="primary.main" fontWeight="bold">
              {totalDays}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Total Days
            </Typography>
          </Box>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h4" color="primary.main" fontWeight="bold">
              {formatDate(reservationStart)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Campaign Start
            </Typography>
          </Box>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h4" color="success.main" fontWeight="bold">
              {formatDate(deliveryEnd)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Delivery Complete
            </Typography>
          </Box>
        </Box>
      </Box>
    </Paper>
  );
};

export default CampaignTimeline;
